<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useCategories } from '../composables/useCategories'
import NavActions from './NavActions.vue'
import LogoText from './common/LogoText.vue'
import type { Category } from '../types/common'
import { isMobile } from '@/utils/common'
import { useAuthStore } from '@/stores/authStore'

// defineOptions({ name: 'AppHeader' })

const { categories, parentCategories, loadCategories, loadParentCategories } = useCategories()

const authStore = useAuthStore()
const isAdmin = computed(() => authStore.isAdmin)

const sortedParentCategories = computed(() => {
  // [beauty, fashion, home, kids]
  // [computers, electronic, motors, outfoor]
  return parentCategories.value.sort((a, b) => a.name.localeCompare(b.name))
})

const headerRef = ref<HTMLElement | null>(null)

// Keep header height as CSS var for full-width mega menu positioning
const updateHeaderHeight = () => {
  const h = headerRef.value?.offsetHeight || 64
  document.documentElement.style.setProperty('--header-height', `${h}px`)
}

// Categories menu open/close with hover buffering
const showCategoriesMenu = ref(false)
let categoriesHideTimer: number | null = null

const onCategoriesEnter = () => {
  if (categoriesHideTimer) {
    clearTimeout(categoriesHideTimer)
    categoriesHideTimer = null
  }
  showCategoriesMenu.value = true
}

const onCategoriesLeave = () => {
  if (categoriesHideTimer) {
    clearTimeout(categoriesHideTimer)
  }
  categoriesHideTimer = window.setTimeout(() => {
    showCategoriesMenu.value = false
  }, 120)
}

// Handle click outside to close dropdowns
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement

  // Close dropdowns if clicking outside
  if (!target.closest('.categories-trigger')) {
    showCategoriesMenu.value = false
  }
}

onMounted(() => {
  // Add click outside listener
  document.addEventListener('click', handleClickOutside)
  updateHeaderHeight()
  window.addEventListener('resize', updateHeaderHeight)

  // Load categories and parent categories
  void loadCategories()
  void loadParentCategories()
})

onUnmounted(() => {
  // Remove click outside listener
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', updateHeaderHeight)
})

// Categories mega menu (non-clickable tab, show tree on hover)
// Use shared categories cache
// const categories = ref<Category[]>([])

// Use the same logic as useCategories composable
const getParentId = (c: Category): string | undefined => c.parent_id || undefined

// Make these reactive to categories changes
// parentCategories provided by useCategories (already filtered from API)

const subcategoriesByParent = computed(() => {
  const map: Record<string, Category[]> = {}
  categories.value.forEach((c) => {
    const pid = getParentId(c)
    if (pid) {
      if (!map[pid]) map[pid] = []
      map[pid].push(c)
    }
  })
  return map
})
</script>

<template>
  <!-- Header/Navigation -->
  <header class="app-header" ref="headerRef">
    <nav class="nav-container">
      <div class="nav-brand">
        <router-link to="/" class="brand-link">
          <img src="/icon-32x32.png" alt="TakeFav" />
          <LogoText size="medium" />
        </router-link>
        <NavActions v-if="isMobile()" />
      </div>

      <div class="nav-menu">
        <router-link to="/public" v-if="isAdmin" class="nav-link">Home</router-link>
        <router-link to="/products" class="nav-link">Store</router-link>
        <!-- Categories as hover tab only (no direct routing) -->
        <div
          class="nav-link categories-trigger"
          tabindex="0"
          @mouseenter="onCategoriesEnter"
          @mouseleave="onCategoriesLeave"
        >
          <span class="categories-link">Categories</span>
          <div
            v-if="showCategoriesMenu"
            class="mega-menu"
            :class="{ open: showCategoriesMenu }"
            @mouseenter="onCategoriesEnter"
            @mouseleave="onCategoriesLeave"
          >
            <div class="mega-inner">
              <div class="mega-columns">
                <div v-for="parent in sortedParentCategories" :key="parent.id" class="mega-column">
                  <div class="mega-parent">{{ parent.name }}</div>
                  <div class="mega-children">
                    <router-link
                      v-for="child in subcategoriesByParent[parent.id] || []"
                      :key="child.id"
                      class="mega-child"
                      :to="`/category/${child.slug || child.id}`"
                      @click="showCategoriesMenu = false"
                    >
                      {{ child.name }}
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <router-link to="/articles" class="nav-link">Articles</router-link>
        <router-link to="/favorites" class="nav-link">Favorites</router-link>
      </div>

      <NavActions v-if="!isMobile()" />
    </nav>
  </header>
</template>

<style lang="scss" scoped>
// ===== HEADER COMPONENT =====
.app-header {
  background: #f8f8f8;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;

  @media (max-width: 768px) {
    padding-top: 8px;
  }
}

// ===== NAVIGATION CONTAINER =====
.nav-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 1rem;
  display: flex;
  align-items: center;

  @media (min-width: 768px) {
    justify-content: space-between;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 0.5rem 0.75rem;
    gap: 0.1px;
  }
}

// ===== BRAND SECTION =====
.nav-brand {
  display: flex;
  align-items: center;
  gap: 1rem;

  .brand-link {
    text-decoration: none;
    display: flex;
    gap: 0.2rem;
    align-items: center;
  }

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 96%;
  }
}

// ===== NAVIGATION MENU =====
.nav-menu {
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
    width: 84%;
    justify-content: space-between;
    margin-top: 5px;
    width: 100%;

    .nav-link {
      width: inherit;
      text-align: center;
      background-color: #fefefe;
    }
  }
}

.nav-link {
  text-decoration: none;
  color: #888;
  font-weight: 600;
  transition: color 0.2s;

  &:hover,
  &.router-link-active {
    color: #333;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0.2rem 0.5rem;
  }
}

// ===== CATEGORIES MEGA MENU =====
.categories-trigger {
  position: relative;
  display: flex;
  align-items: center;

  &:hover .mega-menu,
  &:focus-within .mega-menu {
    display: block;
  }

  .mega-menu.open {
    display: block;
  }

  .categories-link {
    text-decoration: none;
    color: inherit;
    font-weight: inherit;
    font-size: inherit;

    &:hover {
      color: #4f46e5;
    }
  }
}

.mega-menu {
  display: none;
  position: fixed;
  left: 0;
  right: 0;
  top: var(--header-height, 64px);
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
  padding: 1.25rem 0;
  z-index: 1000;

  @media (max-width: 768px) {
    left: 0;
    right: 0;
    width: 100vw;
    height: calc(100vh - 140px);
  }
}

.mega-inner {
  padding: 0 6rem;
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem 1rem 3rem;
    height: 100%;
  }
}

.mega-columns {
  display: grid;
  grid-template-columns: repeat(4, minmax(200px, 1fr));
  gap: 1.5rem 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(45%, 1fr));
    height: 100%;
    overflow: hidden;
    overflow-y: auto;
    text-align: left;
  }

  .mega-column {
    .mega-parent {
      font-weight: 500;
      color: #222;
      margin-bottom: 0.5rem;
      padding-bottom: 0.2rem;
      border-bottom: 1px solid #f4f4f4;
      font-size: 1.1rem;
    }

    .mega-children {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;

      .mega-child {
        text-decoration: none;
        color: #444;
        font-size: 0.95rem;
        border-radius: 6px;
        padding: 4px 6px;
        font-weight: 300;

        &:hover {
          color: #000;
          font-weight: 350;
        }
      }
    }
  }
}

// ===== MOBILE MEGA MENU CONTENT =====
.mega-menu-content {
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }
}

.mega-menu-section {
  h3 {
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }

  ul {
    @media (max-width: 768px) {
      gap: 0.5rem;
    }
  }

  li a {
    @media (max-width: 768px) {
      font-size: 0.875rem;
      padding: 0.5rem;
    }
  }
}
</style>
